/**
 * @file usePermissions.js
 * Hook per accedere ai permessi dell'utente da qualunque punto dell'app.
 *
 * Utilizzo:
 *   const { can, permissions, refreshPermissions, isLoading } = usePermissions();
 *
 *   // Controllo singolo permesso (null-safe):
 *   if (can('canEditCells')) { ... }
 *
 *   // Controllo multiplo:
 *   const canModify = can('canEditCells') && can('canPaste');
 *
 *   // Costruzione oggetto permissions per componenti che lo richiedono come prop:
 *   const gridPerms = buildGridPermissions();
 *
 * La funzione `can(key)` garantisce:
 *   - Se i permessi non sono ancora caricati (null) → false
 *   - Se la chiave non è presente nell'oggetto → false
 *   - Se il valore non è esattamente `true` → false
 *   - SOLO se presente E === true → true
 */

import { useContext } from 'react';
import { PermissionsContext } from '../context/PermissionsContext';

/**
 * Hook che espone il sistema di permessi globale.
 *
 * @returns {{
 *   permissions: Object|null,
 *   can: (key: string) => boolean,
 *   refreshPermissions: () => Promise<void>,
 *   isLoading: boolean,
 *   buildGridPermissions: () => Object
 * }}
 */
export function usePermissions() {
  const context = useContext(PermissionsContext);

  if (!context) {
    throw new Error('usePermissions deve essere usato dentro <PermissionsProvider>');
  }

  const { permissions, can, refreshPermissions, isLoading } = context;

  /**
   * Costruisce l'oggetto permissions nel formato atteso da ExcelGrid.
   * Ogni flag è derivato tramite `can()` con null-safety garantita.
   *
   * Questo metodo è una convenience per i consumer che passano l'oggetto
   * come prop al componente ExcelGrid (o simili).
   *
   * @returns {Object} Oggetto con i flag booleani per la griglia.
   */
  const buildGridPermissions = () => ({
    canEditCells: can('canEditCells'),
    canCopy: can('canCopy'),
    canCut: can('canCut'),
    canPaste: can('canPaste'),
    canClearCells: can('canClearCells'),
    canDeleteRow: can('canDeleteRow'),
    canEditNote: can('canEditNote'),
    canMerge: can('canMerge'),
  });

  return {
    permissions,
    can,
    refreshPermissions,
    isLoading,
    buildGridPermissions,
  };
}
